import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Alert, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS as BaseIOS } from './shared/MenuLayout';
import { PharmacySelect, PharmacyTabs } from './shared/PharmacyUI';
import { UsuariosOverview } from './shared/UsuariosOverview';
import './shared/Usuarios.css';
const IOS={...BaseIOS,blue:"#548EE6",green:"#65AD92",purple:"#AC98CC",red:"#D48C95",orange:"#D6AC72",label:"#34435A",label2:"#8290A5",separator:"#E7ECF3"};


// ── Helpers ───────────────────────────────────────────────────────────────────
const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

// ── Componentes base ──────────────────────────────────────────────────────────
const Lbl = ({children}) => (
  <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,letterSpacing:'0.07em',textTransform:'uppercase',mb:0.5}}>{children}</Typography>
);

const Modal = ({open,onClose,children,w=500}) => <Dialog open={!!open} onClose={onClose} maxWidth={false} aria-label="Gestión de usuarios" PaperProps={{className:'users-dialog',sx:{width:w,maxWidth:'calc(100% - 32px)',borderRadius:3}}}>{children}</Dialog>;

const ModalHeader = ({title,subtitle,onClose,color=IOS.blue,icon}) => (
  <Box sx={{px:2.5,pt:2.5,pb:2,borderBottom:`1px solid ${IOS.separator}`,flexShrink:0,
    display:'flex',alignItems:'center',justifyContent:'space-between'}}>
    <Box sx={{display:'flex',alignItems:'center',gap:1.2}}>
      <Box sx={{width:34,height:34,borderRadius:'10px',bgcolor:`${color}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>{icon}</Box>
      <Box>
        <Typography sx={{fontWeight:700,fontSize:'0.92rem',color:IOS.label}}>{title}</Typography>
        {subtitle&&<Typography sx={{fontSize:'0.7rem',color:IOS.label2}}>{subtitle}</Typography>}
      </Box>
    </Box>
    <Box component="button" aria-label="Cerrar ventana" onClick={onClose}
      sx={{width:28,height:28,borderRadius:'50%',border:'none',bgcolor:'#E5E5EA',cursor:'pointer',
        display:'flex',alignItems:'center',justifyContent:'center','&:hover':{bgcolor:'#D1D1D6'}}}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
    </Box>
  </Box>
);

const InputField = ({label,value,onChange,placeholder,disabled,type='text',name}) => (
  <Box>
    <Lbl>{label}</Lbl>
    <input aria-label={label} type={type} name={name} value={value||''} onChange={onChange} placeholder={placeholder} disabled={disabled}
      style={{width:'100%',height:36,padding:'0 10px',border:`1px solid ${IOS.separator}`,borderRadius:8,
        fontSize:'0.8rem',color:IOS.label,backgroundColor:disabled?IOS.bg:IOS.surface,
        outline:'none',fontFamily:'inherit',boxSizing:'border-box',
        opacity:disabled?0.6:1}}
      onFocus={e=>{if(!disabled)e.target.style.borderColor=IOS.blue;}}
      onBlur={e=>e.target.style.borderColor=IOS.separator}/>
  </Box>
);

// ── Toast notification (reemplaza alert()) ───────────────────────────────────
const TOAST_CFG = {
  error:   {bg:'#FFF0EF',bdr:'#FFD4D1',txt:'#C0392B',dot:'#FF3B30'},
  warning: {bg:'#FFF8E6',bdr:'#FFE9A0',txt:'#8B6914',dot:'#FF9500'},
  success: {bg:'#EDFAF2',bdr:'#B7EFD0',txt:'#1A6B3C',dot:'#34C759'},
};

const Toast = ({msg,tipo='success',onClose}) => {
  React.useEffect(()=>{ const t=setTimeout(onClose,3500); return()=>clearTimeout(t); },[onClose]);
  const cfg = TOAST_CFG[tipo]||TOAST_CFG.success;
  return (
    <Box sx={{position:'fixed',top:20,right:20,zIndex:99999,maxWidth:360,
      bgcolor:cfg.bg,borderRadius:'14px',p:1.8,border:`1px solid ${cfg.bdr}`,
      boxShadow:'0 8px 24px rgba(0,0,0,0.12)',display:'flex',alignItems:'flex-start',gap:1.2,
      animation:'slideIn 0.3s ease',
      '@keyframes slideIn':{'0%':{transform:'translateX(100%)',opacity:0},'100%':{transform:'translateX(0)',opacity:1}}}}>
      <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:cfg.dot,flexShrink:0,mt:0.5}}/>
      <Typography sx={{fontSize:'0.8rem',color:cfg.txt,lineHeight:1.5,flex:1}}>{msg}</Typography>
      <Box component="button" onClick={onClose}
        sx={{width:18,height:18,borderRadius:'50%',border:'none',bgcolor:'rgba(0,0,0,0.08)',
          cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={cfg.txt} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </Box>
    </Box>
  );
};

// ── Dropdown rol custom (sin flash negro del select nativo) ──────────────────
const RolDropdown = ({roles,rolSel,setRolSel,mb=0}) => <Box sx={{mb}}><PharmacySelect label="Rol del usuario" value={rolSel??''} onChange={e=>setRolSel(e.target.value?Number(e.target.value):null)} w="100%"><option value="">Seleccionar rol…</option>{roles.map(r=><option key={r.IDROL} value={r.IDROL}>{r.DESCRIPCION}</option>)}</PharmacySelect></Box>;

// ══════════════════════════════════════════════════════════════════════════════
// TABS

const Usuarios = () => {
  const navigate   = useNavigate();
  const user       = authService.getCurrentUser();
  const ueId=Number(user?.ueIdEstablecimiento||user?.UeIdEstablecimiento||0);
  const nf         = user?.nombreCompleto||user?.usuario||'';
  const [access,setAccess]=useState({puedeEscribir:false,puedeAsignarRoles:false});
  const [accessNotice,setAccessNotice]=useState('');
  const [accessAttempt,setAccessAttempt]=useState(0);
  const esAdmin=access.puedeAsignarRoles;
  const [errorUsuarios,setErrorUsuarios]=useState(''),[errorSesiones,setErrorSesiones]=useState('');
  const usersRequest=useRef(0),activityRequest=useRef(0);
  useEffect(()=>{
    let active=true;setAccessNotice('');
    api.get('/usuarios/acceso').then(r=>{
      if(!r.data.success)throw new Error('No se pudieron comprobar los permisos.');
      if(active)setAccess(r.data);
    }).catch(e=>{
      if(!active)return;
      if(e.response?.status===404){
        // Compatibility controls only; every mutation is still authorized by the server.
        const previousUser=authService.getCurrentUser();
        const previousAdmin=(previousUser?.rol||'').toUpperCase().includes('ADMIN');
        setAccess({legacy:true,puedeEscribir:true,puedeAsignarRoles:previousAdmin,userId:previousUser?.idUser});
        setAccessNotice('El servidor todavía no tiene la actualización de Usuarios. Puedes usar las opciones anteriores; el nuevo resumen y el historial requieren actualizar el backend.');
      }else{
        setAccess({puedeEscribir:false,puedeAsignarRoles:false});
        setAccessNotice(e.response?.status===403?'Tu cuenta no tiene permiso para administrar usuarios.':'No se pudieron comprobar los permisos. Reintenta para habilitar las opciones autorizadas.');
      }
    });
    return()=>{active=false;};
  },[accessAttempt]);
  useEffect(()=>()=>{usersRequest.current++;activityRequest.current++;},[]);
  const handleLogout = () => { authService.logout(); navigate('/login'); };
  const [tab,setTab] = useState('lista');
  const [toast,setToast]   = useState(null); // {msg,tipo}
  const showToast = (msg,tipo='success') => setToast({msg,tipo});

  // ── TAB LISTA ──────────────────────────────────────────────────────────────
  const [fechaSesiones,setFechaSesiones] = useState(hoy());
  const [sesiones,setSesiones]           = useState([]);
  const [cargSes,setCargSes]             = useState(true);
  const [usuarios,setUsuarios]           = useState([]);
  const [cargUs,setCargUs]               = useState(true);
  const [resetModal,setResetModal]       = useState(false);
  const [resetFila,setResetFila]         = useState(null);
  const [reseteando,setReseteando]       = useState(false);
  const [bajaModal,setBajaModal]         = useState(false);
  const [bajaFila,setBajaFila]           = useState(null);
  const [dandoBaja,setDandoBaja]         = useState(false);
  const [disposModal,setDisposModal]     = useState(false);
  const [disposFila,setDisposiFila]      = useState(null);
  const [disposTipo,setDisposTipo]       = useState(null);
  const [cambiandoDisp,setCambiandoDisp] = useState(false);

  const cargarSesiones = useCallback(async (silent=false) => {
    const request=++activityRequest.current;
    if(!fechaSesiones){setSesiones([]);setCargSes(false);setErrorSesiones('');return;}
    if(!silent)setCargSes(true);setErrorSesiones('');
    try{const r=await api.get('/usuarios/resumen-actividad',{params:{fecha:fechaSesiones}});if(!r.data.success)throw new Error(r.data.message);if(request===activityRequest.current)setSesiones(r.data.data||[]);}
    catch(e){if(request===activityRequest.current)setErrorSesiones(e.response?.status===404?'El resumen de actividad aún no está publicado en el servidor. Es necesario actualizar el backend; no falta un procedimiento almacenado.':e.response?.data?.message||'No se pudo cargar la actividad.');}
    finally{if(request===activityRequest.current)setCargSes(false);}
  },[fechaSesiones]);
  const cargarUsuarios = useCallback(async (silent=false) => {
    const request=++usersRequest.current;if(!silent)setCargUs(true);setErrorUsuarios('');
    try{const r=await api.get('/usuarios/listar',{params:ueId>0?{ue:ueId}:{}});if(!r.data.success)throw new Error(r.data.message);if(request===usersRequest.current)setUsuarios(r.data.data||[]);}
    catch(e){if(request===usersRequest.current)setErrorUsuarios(e.response?.data?.message||'No se pudieron cargar los usuarios.');}
    finally{if(request===usersRequest.current)setCargUs(false);}
  },[ueId]);
  useEffect(()=>{cargarSesiones();},[cargarSesiones]);
  useEffect(()=>{if(tab==='lista')cargarUsuarios();},[tab,cargarUsuarios]);
  useEffect(()=>{if(tab!=='lista')return;const refresh=()=>{if(document.visibilityState==='visible'){cargarUsuarios(true);cargarSesiones(true);}};const timer=setInterval(refresh,60000);document.addEventListener('visibilitychange',refresh);return()=>{clearInterval(timer);document.removeEventListener('visibilitychange',refresh);};},[tab,cargarUsuarios,cargarSesiones]);

  // Dar de baja
  const handleBaja = async () => {
    if(!bajaFila) return;
    setDandoBaja(true);
    try {
      const r = await api.post('/usuarios/eliminar',{idUsuario:bajaFila.USER_IDUSER});
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success){ setBajaModal(false); cargarUsuarios(); }
    } catch(e){showToast(e.response?.data?.message || 'No se pudo completar la operación.','error');} finally{setDandoBaja(false);}
  };

  // Permiso dispositivo
  const handlePermiso = async () => {
    if(!disposFila||!disposTipo) return;
    setCambiandoDisp(true);
    try {
      const r = await api.post('/usuarios/permiso-dispositivo',{idUsuario:disposFila.USER_IDUSER,tipo:disposTipo});
      showToast(r.data.message, r.data.success?'success':'error');
      if(r.data.success){ setDisposModal(false); cargarUsuarios(); }
    } catch(e){showToast(e.response?.data?.message || 'No se pudo completar la operación.','error');} finally{setCambiandoDisp(false);}
  };

  const [temporaryCredential,setTemporaryCredential] = useState(null);
  // Reset contraseña
  const handleReset = async () => {
    if(!resetFila) return;
    setReseteando(true);
    try {
      const r = await api.post('/usuarios/resetear-password',{idUsuario:resetFila.USER_IDUSER});
      if(r.data.success){ setResetModal(false); setTemporaryCredential({user:resetFila.USER_USUARIO,password:r.data.temporaryPassword}); showToast(r.data.message,'success'); }
      else showToast(r.data.message, r.data.success?'success':'error');
    } catch(e){showToast(e.response?.data?.message || 'No se pudo completar la operación.','error');} finally{setReseteando(false);}
  };

  // ── TAB REGISTRO ──────────────────────────────────────────────────────────
  const [dniBusq,setDniBusq]   = useState('');
  const [tipoDni,setTipoDni]   = useState('DNI');
  const [buscandoDni,setBuscDni] = useState(false);
  const [personaData,setPersona] = useState(null);
  const [usuario,setUsuario]   = useState('');
  const [roles,setRoles]       = useState([]);
  const [rolSel,setRolSel]     = useState(null);
  const [permisos,setPermisos] = useState([]);
  const [ipress,setIpress]     = useState([]);
  const [ipressBusq,setIpressBusq] = useState('');
  const [ipressSel,setIpressSel]   = useState(null);
  const [guardando,setGuardando]   = useState(false);
  const [modoEdicion,setModoEdicion] = useState(false);
  const [usuarioEditar,setUsuarioEditar] = useState(null);

  useEffect(()=>{
    api.get('/usuarios/roles').then(r=>{ if(r.data.success) setRoles(r.data.data||[]); }).catch(console.error);
  },[]);

  useEffect(()=>{
    if(!rolSel) return;
    api.get('/usuarios/permisos-rol',{params:{idRol:rolSel}})
      .then(r=>{ if(r.data.success) setPermisos(r.data.data||[]); }).catch(console.error);
  },[rolSel]);

  useEffect(()=>{
    const t = setTimeout(()=>{
      if(ipressBusq.length<2) return;
      api.get('/usuarios/listar-ipress',{params:{buscar:ipressBusq}})
        .then(r=>{ if(r.data.success) setIpress(r.data.data||[]); }).catch(console.error);
    },400);
    return ()=>clearTimeout(t);
  },[ipressBusq]);

  const buscarDni = async () => {
    if(dniBusq.length<6) return;
    setBuscDni(true); setPersona(null); setUsuario('');
    try {
      const r = await api.get('/usuarios/buscar-por-dni',{params:{dni:dniBusq}});
      if(r.data.success && r.data.sinUsuario){
        setPersona(r.data.data);
        // Sugerir nombre
        const r2 = await api.get('/usuarios/sugerir-nombre',{params:{dni:dniBusq}});
        if(r2.data.success) setUsuario((r2.data.usuarioSugerido||'').toUpperCase());
      } else {
        showToast(r.data.message||'No encontrado','warning');
      }
    } catch(e){showToast(e.response?.data?.message || 'No se pudo completar la operación.','error');} finally{setBuscDni(false);}
  };

  // Verificar si una fila es de un usuario administrador
  const filaEsAdmin = (row) => (row.ROL||'').toUpperCase().includes('ADMIN');

  // Verificar si puedo operar sobre esta fila
  const puedeOperar = (row) => access.puedeEscribir && (esAdmin || (!filaEsAdmin(row) && (access.legacy || String(row.USER_IDUSER)!==String(access.userId))));

  const editarUsuario = (row) => {
    setModoEdicion(true); setUsuarioEditar(row);
    setDniBusq(row.DNI||''); setUsuario(row.USER_USUARIO||'');
    setPersona({apellidoPaterno:row.PERSONAL?.split(' ')[0]||'',apellidoMaterno:row.PERSONAL?.split(' ')[1]||'',nombres:row.PERSONAL?.split(' ').slice(2).join(' ')||''});
    setRolSel(row.IDROL); setIpressSel({id:row.USER_ID_UE,nombre:row.ESTABLECIMIENTO});
    setTab('registro');
  };

  const guardar = async () => {
    if(!personaData||!usuario||!rolSel||!ipressSel) return;
    setGuardando(true);
    try {
      if(modoEdicion && usuarioEditar){
        const r = await api.put('/usuarios/actualizar',{
          idUsuario:usuarioEditar.USER_IDUSER, idUe:ipressSel.id, nuevoRol:rolSel, privilegio:'NORMAL'
        });
        if(r.data.success){ showToast(r.data.message, r.data.success?'success':'error'); limpiarForm(); setTab('lista'); cargarUsuarios(); }
        else showToast(r.data.message, r.data.success?'success':'error');
      } else {
        const r = await api.post('/usuarios/crear',{
          idPersonal:personaData.idPersonal, idUe:ipressSel.id, usuario, idRol:rolSel, privilegio:'NORMAL', ipress:ipressSel.nombre
        });
        if(r.data.success){ setTemporaryCredential({user:usuario,password:r.data.temporaryPassword}); showToast(r.data.message,'success'); limpiarForm(); setTab('lista'); cargarUsuarios(); }
        else showToast(r.data.message, r.data.success?'success':'error');
      }
    } catch(e){showToast(e.response?.data?.message || 'No se pudo completar la operación.','error');} finally{setGuardando(false);}
  };

  const limpiarForm = () => {
    setDniBusq(''); setPersona(null); setUsuario(''); setRolSel(null);
    setPermisos([]); setIpressSel(null); setIpressBusq(''); setIpress([]);
    setModoEdicion(false); setUsuarioEditar(null);
  };

  return (
    <>
      <Box className="users-page pharmacy-page" sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
        <HeaderIOS titulo="Usuarios" subtitulo="Integra"
          onHome={()=>navigate('/dashboard')} onLogout={handleLogout}
          nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()}/>

        <Box className="users-content" sx={{width:'100%',boxSizing:'border-box',maxWidth:1600,mx:'auto',px:{xs:2,sm:4},pt:2.5,pb:6}}>

          <div className="pharmacy-tabs-row"><PharmacyTabs value={tab==='lista'?0:1} onChange={value=>setTab(value===0?'lista':'registro')} options={[{label:'Usuarios'},...((access.puedeEscribir&&(access.puedeAsignarRoles||access.legacy))||tab==='registro'?[{label:modoEdicion?'Editar usuario':'Nuevo usuario'}]:[])]}/></div>
          {accessNotice&&<Alert severity="warning" sx={{mb:2,borderRadius:3}} action={<Button onClick={()=>setAccessAttempt(v=>v+1)}>Reintentar</Button>}>{accessNotice}</Alert>}
          <div className="users-page-heading"><span>ADMINISTRACIÓN · USUARIOS</span><h1>Personas y accesos</h1><p>Consulta la presencia reciente, revisa los accesos y administra las cuentas de tu unidad ejecutora.</p></div>
          {tab==='lista'&&<UsuariosOverview users={usuarios} loading={cargUs} error={errorUsuarios} onRefresh={()=>cargarUsuarios()} data={sesiones} chartLoading={cargSes} chartError={errorSesiones} date={fechaSesiones} onDate={setFechaSesiones} onRefreshChart={()=>cargarSesiones()} canOperate={puedeOperar} onEdit={editarUsuario} onReset={row=>{setResetFila(row);setResetModal(true);}} onDevice={row=>{setDisposiFila(row);setDisposTipo(null);setDisposModal(true);}} onDelete={row=>{setBajaFila(row);setBajaModal(true);}}/>}

          {/* ══ TAB REGISTRO ══ */}
          {tab==='registro'&&(
            <Grid container spacing={2}>

              {/* Col izquierda — DNI + datos persona */}
              <Grid item xs={12} lg={5}>
                <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2.5,
                  border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}>
                  <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',mb:2}}>
                    <Box sx={{display:'flex',alignItems:'center',gap:1}}>
                      <Box sx={{width:30,height:30,borderRadius:'9px',bgcolor:`${IOS.blue}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </Box>
                      <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label}}>
                        {modoEdicion?'Editar Usuario':'Nuevo Usuario'}
                      </Typography>
                    </Box>
                    {modoEdicion&&(
                      <Box component="button" onClick={limpiarForm}
                        sx={{px:1,py:0.3,borderRadius:'8px',border:`1px solid ${IOS.separator}`,
                          bgcolor:IOS.bg,cursor:'pointer',fontSize:'0.72rem',color:IOS.label2}}>
                        + Nuevo
                      </Box>
                    )}
                  </Box>

                  {/* Tipo + DNI */}
                  {!modoEdicion&&(
                    <Box sx={{mb:1.5}}>
                      <Lbl>Documento de identidad</Lbl>
                      <Box sx={{display:'flex',gap:1}}>
                        <Box sx={{display:'flex',borderRadius:8,border:`1px solid ${IOS.separator}`,overflow:'hidden',flexShrink:0}}>
                          {['DNI','CE'].map(t=>(
                            <Box key={t} onClick={()=>setTipoDni(t)}
                              sx={{px:1.2,height:36,display:'flex',alignItems:'center',cursor:'pointer',
                                bgcolor:tipoDni===t?IOS.blue:'transparent',transition:'all 0.15s'}}>
                              <Typography sx={{fontSize:'0.72rem',fontWeight:700,color:tipoDni===t?'white':IOS.label2}}>{t}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <input value={dniBusq} onChange={e=>setDniBusq(e.target.value)}
                          onKeyDown={e=>e.key==='Enter'&&buscarDni()}
                          placeholder={tipoDni==='DNI'?'8 dígitos...':'Carnet extranjería...'}
                          style={{flex:1,height:36,padding:'0 10px',border:`1px solid ${IOS.separator}`,borderRadius:8,
                            fontSize:'0.8rem',color:IOS.label,backgroundColor:IOS.surface,outline:'none',fontFamily:'inherit'}}
                          onFocus={e=>e.target.style.borderColor=IOS.blue}
                          onBlur={e=>e.target.style.borderColor=IOS.separator}/>
                        <Box component="button" onClick={buscarDni} disabled={buscandoDni}
                          sx={{height:36,px:1.5,borderRadius:'10px',border:'none',bgcolor:IOS.blue,cursor:'pointer',
                            display:'flex',alignItems:'center',gap:0.6,'&:hover':{filter:'brightness(0.9)'}}}>
                          {buscandoDni
                            ?<Box sx={{width:12,height:12,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'white',animation:'spin 0.8s linear infinite','@keyframes spin':{'0%':{transform:'rotate(0deg)'},'100%':{transform:'rotate(360deg)'}}}}/>
                            :<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                          }
                          <Typography sx={{fontSize:'0.78rem',fontWeight:700,color:'white'}}>Buscar</Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Datos persona */}
                  <Box sx={{display:'flex',flexDirection:'column',gap:1.2}}>
                    <Grid container spacing={1.2}>
                      <Grid item xs={6}><InputField label="Ap. Paterno" value={personaData?.apellidoPaterno} disabled placeholder="—"/></Grid>
                      <Grid item xs={6}><InputField label="Ap. Materno" value={personaData?.apellidoMaterno} disabled placeholder="—"/></Grid>
                    </Grid>
                    <InputField label="Nombres" value={personaData?.nombres} disabled placeholder="—"/>
                    {/* Input usuario + política */}
                    <Box>
                      <Lbl>Nombre de usuario</Lbl>
                      <Box sx={{border:`1px solid ${usuario?IOS.blue:IOS.separator}`,borderRadius:'10px',overflow:'hidden',
                        boxShadow:usuario?`0 0 0 3px ${IOS.blue}10`:'none',transition:'all 0.2s'}}>
                        <input value={usuario} readOnly
                          placeholder="Se genera al buscar DNI..."
                          style={{width:'100%',height:36,padding:'0 10px',border:'none',
                            fontSize:'0.88rem',color:IOS.blue,backgroundColor:IOS.bg,
                            outline:'none',fontFamily:'monospace',boxSizing:'border-box',
                            textTransform:'uppercase',fontWeight:700,cursor:'default'}}/>
                        <Box sx={{px:1.2,py:0.8,bgcolor:`${IOS.blue}06`,borderTop:`1px dashed ${IOS.blue}20`,
                          display:'flex',gap:0.8,alignItems:'flex-start'}}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:2}}>
                            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          <Typography sx={{fontSize:'0.58rem',color:`${IOS.blue}bb`,lineHeight:1.5}}>
                            <strong>Política:</strong> 1 letra del nombre + apellido paterno. Letras/números si ya existe. ISO 9001 · 27001.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Col derecha — IPRESS + Rol + Permisos + Guardar */}
              <Grid item xs={12} lg={7}>

                {/* IPRESS */}
                <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2,
                  border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)',mb:2}}>
                  <Box sx={{display:'flex',alignItems:'center',gap:1,mb:1.5}}>
                    <Box sx={{width:28,height:28,borderRadius:'8px',bgcolor:`${IOS.orange}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                    </Box>
                    <Typography sx={{fontWeight:700,fontSize:'0.85rem',color:IOS.label}}>IPRESS</Typography>
                    {ipressSel&&<Box sx={{ml:'auto',px:0.8,py:0.2,bgcolor:`${IOS.green}10`,borderRadius:'20px',border:`1px solid ${IOS.green}20`}}>
                      <Typography sx={{fontSize:'0.62rem',fontWeight:700,color:IOS.green}}>✓ Seleccionada</Typography>
                    </Box>}
                  </Box>
                  {ipressSel&&(
                    <Box sx={{p:1,bgcolor:`${IOS.green}06`,borderRadius:'8px',border:`1px solid ${IOS.green}15`,mb:1,
                      display:'flex',alignItems:'center',justifyContent:'space-between',gap:1}}>
                      <Typography sx={{fontSize:'0.75rem',fontWeight:600,color:IOS.label,flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ipressSel.nombre}</Typography>
                      <Box component="button" onClick={()=>setIpressSel(null)}
                        sx={{width:18,height:18,borderRadius:'50%',border:'none',bgcolor:'#E5E5EA',cursor:'pointer',
                          display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{position:'relative'}}>
                    <Box sx={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:IOS.label2,pointerEvents:'none'}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    </Box>
                    <input value={ipressBusq} onChange={e=>setIpressBusq(e.target.value)}
                      placeholder="Buscar IPRESS..."
                      style={{width:'100%',height:34,paddingLeft:26,paddingRight:10,border:`1px solid ${IOS.separator}`,borderRadius:8,
                        fontSize:'0.78rem',color:IOS.label,backgroundColor:IOS.bg,outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
                      onFocus={e=>e.target.style.borderColor=IOS.blue}
                      onBlur={e=>e.target.style.borderColor=IOS.separator}/>
                  </Box>
                  {ipress.length>0&&(
                    <Box sx={{maxHeight:160,overflowY:'auto',border:`1px solid ${IOS.separator}`,borderRadius:'10px',mt:0.5,
                      '&::-webkit-scrollbar':{width:3},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>
                      {ipress.map((ip,i)=>(
                        <Box key={i} onClick={()=>{setIpressSel({id:ip.UE_ID_ESTABLECIMIENTO||ip.ID_ESTABLECIMIENTO,nombre:ip.UE_NOMBRE_ESTABLECIMIENTO||ip.NOMBRE});setIpressBusq('');setIpress([]);}}
                          sx={{px:1.5,py:0.8,cursor:'pointer',borderBottom:i<ipress.length-1?`1px solid ${IOS.separator}`:'none','&:hover':{bgcolor:IOS.bg}}}>
                          <Typography sx={{fontSize:'0.75rem',color:IOS.label}}>{ip.UE_NOMBRE_ESTABLECIMIENTO||ip.NOMBRE}</Typography>
                          {(ip.UE_CLASIFICACION||ip.CLASIFICACION)&&<Typography sx={{fontSize:'0.62rem',color:IOS.label2}}>{ip.UE_CLASIFICACION||ip.CLASIFICACION}</Typography>}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Rol + Permisos en misma card */}
                <Box sx={{bgcolor:IOS.surface,borderRadius:'16px',p:2,
                  border:`1px solid ${IOS.separator}`,boxShadow:'0 1px 4px rgba(0,0,0,0.07)',mb:2}}>
                  <Box sx={{display:'flex',alignItems:'center',gap:1,mb:1.5}}>
                    <Box sx={{width:28,height:28,borderRadius:'8px',bgcolor:`${IOS.purple}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    </Box>
                    <Typography sx={{fontWeight:700,fontSize:'0.85rem',color:IOS.label}}>Rol y permisos</Typography>
                  </Box>

                  {/* Combo rol — custom sin flash negro */}
                  {/* Si no es admin, filtrar roles de administrador */}
                  <RolDropdown
                    roles={esAdmin ? roles : roles.filter(r=>!r.DESCRIPCION?.toUpperCase().includes('ADMIN'))}
                    rolSel={rolSel} setRolSel={setRolSel} mb={permisos.length>0?1.5:0}/>
                  {!esAdmin&&(
                    <Box sx={{mt:0.8,px:1,py:0.5,bgcolor:`${IOS.orange}08`,borderRadius:'8px',border:`1px solid ${IOS.orange}20`}}>
                      <Typography sx={{fontSize:'0.62rem',color:IOS.orange}}>
                        ⚠ Solo administradores pueden asignar roles de administrador.
                      </Typography>
                    </Box>
                  )}

                  {/* Permisos inline */}
                  {permisos.length>0&&(
                    <Box>
                      <Typography sx={{fontSize:'0.58rem',fontWeight:700,color:IOS.label2,textTransform:'uppercase',letterSpacing:'0.07em',mb:0.8}}>
                        Permisos incluidos
                      </Typography>
                      <Box sx={{display:'flex',flexWrap:'wrap',gap:0.5}}>
                        {permisos.map((p,i)=>(
                          <Box key={i} sx={{px:0.8,py:0.25,bgcolor:`${IOS.purple}08`,borderRadius:'6px',border:`1px solid ${IOS.purple}18`}}>
                            <Typography sx={{fontSize:'0.6rem',fontWeight:600,color:IOS.purple}}>
                              {p.PERM_DEATALLE||p.PERM_DETALLE||p.PERM_PERMISO_DESC||'—'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Botón guardar — compacto a la derecha */}
                <Box sx={{display:'flex',justifyContent:'flex-end'}}>
                  <Box component="button" onClick={guardar}
                    disabled={!personaData||!usuario||!rolSel||!ipressSel||guardando}
                    sx={{height:38,px:2.5,borderRadius:'12px',border:'none',
                      bgcolor:!personaData||!usuario||!rolSel||!ipressSel?'#E5E5EA':IOS.blue,
                      cursor:!personaData||!usuario||!rolSel||!ipressSel?'default':'pointer',
                      display:'flex',alignItems:'center',gap:1,
                      boxShadow:personaData&&usuario&&rolSel&&ipressSel?`0 4px 14px ${IOS.blue}40`:'none',
                      transition:'all 0.2s','&:hover':{filter:'brightness(0.92)'}}}>
                    {guardando
                      ?<Box sx={{width:14,height:14,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'white',animation:'spin 0.8s linear infinite','@keyframes spin':{'0%':{transform:'rotate(0deg)'},'100%':{transform:'rotate(360deg)'}}}}/>
                      :<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={!personaData||!usuario||!rolSel||!ipressSel?IOS.label2:'white'} strokeWidth="2" strokeLinecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                    }
                    <Typography sx={{fontSize:'0.85rem',fontWeight:700,
                      color:!personaData||!usuario||!rolSel||!ipressSel?IOS.label2:'white'}}>
                      {guardando?'Guardando...' : modoEdicion?'Actualizar':'Crear usuario'}
                    </Typography>
                  </Box>
                </Box>

              </Grid>
            </Grid>
          )}

        </Box>
      </Box>

      {/* Toast */}
      {toast&&<Toast msg={toast.msg} tipo={toast.tipo} onClose={()=>setToast(null)}/>}

      {/* Modal reset contraseña */}
      <Modal open={resetModal&&!!resetFila} onClose={()=>setResetModal(false)} w={360}>
        <ModalHeader title="Resetear contraseña" onClose={()=>setResetModal(false)} color={IOS.orange}
          subtitle={resetFila?.USER_USUARIO}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}/>
        <Box sx={{p:2.5}}>
          <Box sx={{p:1.5,bgcolor:'#FFF8E6',borderRadius:'10px',border:'1px solid #FFE9A0',mb:2}}>
            <Typography sx={{fontSize:'0.78rem',color:'#8B6914',lineHeight:1.6}}>
              Se generará una contraseña aleatoria que podrás copiar al terminar. Compártela con el usuario por un canal seguro.
            </Typography>
          </Box>
          <Box sx={{display:'flex',gap:1.5}}>
            <Box component="button" onClick={()=>setResetModal(false)}
              sx={{flex:1,height:36,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',fontWeight:600,color:IOS.label2}}>
              Cancelar
            </Box>
            <Box component="button" onClick={handleReset} disabled={reseteando}
              sx={{flex:1,height:36,borderRadius:'10px',border:'none',bgcolor:reseteando?'#E5E5EA':IOS.orange,
                cursor:reseteando?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.8}}>
              {reseteando&&<Box sx={{width:12,height:12,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'white',animation:'spin 0.8s linear infinite','@keyframes spin':{'0%':{transform:'rotate(0deg)'},'100%':{transform:'rotate(360deg)'}}}}/>}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:reseteando?'#8E8E93':'white'}}>
                {reseteando?'Reseteando...':'Sí, resetear'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
      {/* Modal dar de baja */}
      <Modal open={bajaModal&&!!bajaFila} onClose={()=>!dandoBaja&&setBajaModal(false)} w={380}>
        <ModalHeader title="Dar de baja" subtitle={bajaFila?.USER_USUARIO} onClose={()=>!dandoBaja&&setBajaModal(false)} color={IOS.red}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="18" y1="8" x2="23" y2="13"/><line x1="23" y1="8" x2="18" y2="13"/></svg>}/>
        <Box sx={{p:2.5}}>
          <Typography sx={{fontSize:'0.85rem',color:IOS.label,mb:2,lineHeight:1.6}}>
            ¿Deseas dar de baja al usuario <strong>{bajaFila?.USER_USUARIO}</strong> ({bajaFila?.PERSONAL})?
          </Typography>
          <Box sx={{p:1.5,bgcolor:'#FFF0EF',borderRadius:'10px',border:'1px solid #FFD4D1',mb:2.5}}>
            <Typography sx={{fontSize:'0.76rem',color:'#C0392B',fontWeight:600}}>⚠ El usuario no podrá iniciar sesión. Acción reversible desde edición.</Typography>
          </Box>
          <Box sx={{display:'flex',gap:1.5}}>
            <Box component="button" onClick={()=>setBajaModal(false)}
              sx={{flex:1,height:36,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',fontWeight:600,color:IOS.label2}}>Cancelar</Box>
            <Box component="button" onClick={handleBaja} disabled={dandoBaja}
              sx={{flex:1,height:36,borderRadius:'10px',border:'none',bgcolor:dandoBaja?'#E5E5EA':IOS.red,cursor:dandoBaja?'default':'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:0.8}}>
              {dandoBaja&&<Box sx={{width:12,height:12,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'white',animation:'spin 0.8s linear infinite','@keyframes spin':{'0%':{transform:'rotate(0deg)'},'100%':{transform:'rotate(360deg)'}}}}/>}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:dandoBaja?'#8E8E93':'white'}}>{dandoBaja?'Procesando...':'Sí, dar de baja'}</Typography>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Modal permiso dispositivo */}
      <Modal open={disposModal&&!!disposFila} onClose={()=>!cambiandoDisp&&setDisposModal(false)} w={400}>
        <ModalHeader title="Permiso de dispositivo" subtitle={disposFila?.USER_USUARIO} onClose={()=>!cambiandoDisp&&setDisposModal(false)} color={IOS.purple}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>}/>
        <Box sx={{p:2.5}}>
          {disposFila&&(
            <Box sx={{p:1.2,bgcolor:IOS.bg,borderRadius:'10px',border:`1px solid ${IOS.separator}`,mb:1.5,
              display:'flex',alignItems:'center',gap:1}}>
              <Typography sx={{fontSize:'0.7rem',color:IOS.label2}}>Estado actual:</Typography>
              <Box sx={{px:0.8,py:0.2,borderRadius:'20px',
                bgcolor: disposFila.USER_DEVICE_ID==='*'?'#EDFAF2': disposFila.USER_DEVICE_ID==='ABC123'?'#FFF0EF':'#F0EFFF',
                border:`1px solid ${ disposFila.USER_DEVICE_ID==='*'?'#B7EFD0': disposFila.USER_DEVICE_ID==='ABC123'?'#FFD4D1':'#C7C7F0'}`}}>
                <Typography sx={{fontSize:'0.68rem',fontWeight:700,
                  color: disposFila.USER_DEVICE_ID==='*'?IOS.green: disposFila.USER_DEVICE_ID==='ABC123'?IOS.red:IOS.purple}}>
                  { disposFila.USER_DEVICE_ID==='*'?'Acceso libre':
                    disposFila.USER_DEVICE_ID==='ABC123'?'Sin acceso':
                    disposFila.USER_DEVICE_ID===null||disposFila.USER_DEVICE_ID===''?'Un solo equipo':'Equipo registrado'}
                </Typography>
              </Box>
            </Box>
          )}
          <Typography sx={{fontSize:'0.75rem',color:IOS.label2,mb:1.5}}>Selecciona el nuevo tipo de acceso:</Typography>
          <Box sx={{display:'flex',flexDirection:'column',gap:1,mb:2.5}}>
            {[
              {tipo:'libre',    label:'Acceso libre',     desc:'Puede ingresar desde cualquier dispositivo.',           color:IOS.green,  icon:<><circle cx="12" cy="12" r="10"/><path d="M9 11l3 3L22 4"/></>},
              {tipo:'un-equipo',label:'Un solo equipo',   desc:'Se amarrará al primer equipo con que inicie sesión.',    color:IOS.blue,   icon:<><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></>},
              {tipo:'bloqueado',label:'Sin acceso',       desc:'Bloqueado de todos los dispositivos.',                   color:IOS.red,    icon:<><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>},
            ].map(op=>(
              <Box key={op.tipo} onClick={()=>setDisposTipo(op.tipo)}
                sx={{p:1.5,borderRadius:'12px',cursor:'pointer',
                  border:`1.5px solid ${disposTipo===op.tipo?op.color:IOS.separator}`,
                  bgcolor:disposTipo===op.tipo?`${op.color}06`:IOS.bg,
                  transition:'all 0.15s','&:hover':{borderColor:op.color}}}>
                <Box sx={{display:'flex',alignItems:'center',gap:1.2}}>
                  <Box sx={{width:28,height:28,borderRadius:'8px',bgcolor:`${op.color}12`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={op.color} strokeWidth="2" strokeLinecap="round">{op.icon}</svg>
                  </Box>
                  <Box>
                    <Typography sx={{fontSize:'0.8rem',fontWeight:700,color:disposTipo===op.tipo?op.color:IOS.label}}>{op.label}</Typography>
                    <Typography sx={{fontSize:'0.68rem',color:IOS.label2}}>{op.desc}</Typography>
                  </Box>
                  {disposTipo===op.tipo&&<Box sx={{ml:'auto',width:16,height:16,borderRadius:'50%',bgcolor:op.color,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                  </Box>}
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{display:'flex',gap:1.5}}>
            <Box component="button" onClick={()=>setDisposModal(false)}
              sx={{flex:1,height:36,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',fontWeight:600,color:IOS.label2}}>Cancelar</Box>
            <Box component="button" onClick={handlePermiso} disabled={!disposTipo||cambiandoDisp}
              sx={{flex:1,height:36,borderRadius:'10px',border:'none',
                bgcolor:!disposTipo||cambiandoDisp?'#E5E5EA':IOS.purple,cursor:!disposTipo||cambiandoDisp?'default':'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:0.8}}>
              {cambiandoDisp&&<Box sx={{width:12,height:12,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'white',animation:'spin 0.8s linear infinite','@keyframes spin':{'0%':{transform:'rotate(0deg)'},'100%':{transform:'rotate(360deg)'}}}}/>}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:!disposTipo||cambiandoDisp?'#8E8E93':'white'}}>{cambiandoDisp?'Guardando...':'Aplicar'}</Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Dialog open={!!temporaryCredential} onClose={()=>setTemporaryCredential(null)} fullWidth maxWidth="sm" aria-labelledby="temporary-password-title" PaperProps={{sx:{borderRadius:4}}}>
        <DialogTitle id="temporary-password-title">Nueva contraseña</DialogTitle>
        <DialogContent><Typography sx={{mb:2}}>Usuario: {temporaryCredential?.user}</Typography><Typography sx={{mb:2,color:'text.secondary'}}>Copia esta contraseña antes de cerrar. No volverá a mostrarse aquí.</Typography><input aria-label="Nueva contraseña generada" readOnly value={temporaryCredential?.password||''} onFocus={e=>e.target.select()} style={{boxSizing:'border-box',width:'100%',padding:16,border:'1px solid #dfe6ef',borderRadius:12,fontFamily:'monospace',fontSize:16}}/></DialogContent>
        <DialogActions><Button onClick={async()=>{try{await navigator.clipboard.writeText(temporaryCredential.password);showToast('Contraseña copiada','success');}catch{showToast('Selecciona la contraseña y cópiala manualmente','error');}}}>Copiar contraseña</Button><Button onClick={()=>setTemporaryCredential(null)}>Cerrar</Button></DialogActions>
      </Dialog>
    </>
  );
};

export default Usuarios;
